import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Card, Table, Empty, Spin, Tag, Button, Typography, 
  message, Form, Space, Tooltip, Popconfirm, Modal, notification
} from 'antd';
import { 
  EditOutlined, DeleteOutlined,
  InfoCircleOutlined, WarningOutlined, ExclamationCircleOutlined,
  NotificationOutlined
} from '@ant-design/icons';
import { useEvent } from '../../hooks/useEvent';
import { useAuth } from '../../hooks/useAuth';
import { isEventActive as checkEventActive } from '../../utils/eventSource';
import { useAlertNotifications } from '../../contexts/useAlertNotifications';
import { AlertsTimeline } from '../../components/AlertsTimeline/AlertsTimeline';
import alertsService, { 
  type EventAlert, 
  type AlertType, 
  type AlertSeverity,
  type CreateEventAlertRequest,
  type UpdateEventAlertRequest,
  type NotifyBeforeEntry,
  type NotifyBeforeUnit,
  ALERT_TYPE_CONFIG,
  SEVERITY_CONFIG,
  canRoleViewAlert
} from '../../api/alertsService';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { AlertModal } from './AlertModal';
import { showAlertToast } from '../../components/AlertNotifications';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Text } = Typography;

interface AlertFormValues {
  alert_type: AlertType;
  severity: AlertSeverity;
  title: string;
  message: string;
  start_time?: dayjs.Dayjs;
  end_time: dayjs.Dayjs;
  sessions?: string[];
  roles?: string[];
  show_immediately?: boolean;
  notify_on_start?: boolean;
  notify_on_end?: boolean;
  requires_acknowledgment?: boolean;
  notify_before?: { value: number; unit: NotifyBeforeUnit }[];
}

const alertTypeOptions: { value: AlertType; label: string }[] = [
  { value: 'air_temperature', label: '🌡️ Air Temperature' },
  { value: 'rain_risk', label: '🌧️ Rain Risk' },
  { value: 'weather_alert', label: '⚠️ Weather Alert' },
  { value: 'track_condition', label: '🏁 Track Condition' },
  { value: 'system_status', label: '⚙️ System Status' },
  { value: 'general', label: '📢 General' },
];

const severityOptions: { value: AlertSeverity; label: string }[] = [
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' },
];

const getSeverityIcon = (severity: AlertSeverity) => {
  switch (severity) {
    case 'info':
      return <InfoCircleOutlined style={{ color: SEVERITY_CONFIG.info.color }} />;
    case 'warning':
      return <WarningOutlined style={{ color: SEVERITY_CONFIG.warning.color }} />;
    case 'critical':
      return <ExclamationCircleOutlined style={{ color: SEVERITY_CONFIG.critical.color }} />;
    default:
      return null;
  }
};

export const AlertsPage = () => {
  const { selectedEvent } = useEvent();
  const { authState } = useAuth();
  
  // Get alerts and loading from the notification context (handles polling globally)
  const { alerts, loading, refreshAlerts, compactAlerts } = useAlertNotifications();
  
  // Notification API for alert preview (root debug tool)
  const [notificationApi, notificationContextHolder] = notification.useNotification();
  
  // Local refreshing state for silent refresh (button spinner only)
  const [refreshing, setRefreshing] = useState(false);
  
  // Track if initial load has completed (to avoid showing table loading overlay on initial load)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Local error state for CRUD operations
  const [error, setError] = useState<string | null>(null);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState<EventAlert | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [form] = Form.useForm();

  // Table container ref for dynamic scroll height
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableScrollY, setTableScrollY] = useState<number | undefined>(undefined);

  // Check if user has root role
  const hasAdminRole = authState.user?.role === 'root';
  
  // Only root can set roles restriction on alerts
  const isRootUser = authState.user?.role === 'root';
  
  // Get user role for filtering
  const userRole = authState.user?.role || '';
  
  // Filter alerts based on user role
  // Root users see all alerts (so they can manage role-restricted alerts)
  // Other users only see alerts their role can view
  const visibleAlerts = useMemo(() => {
    if (isRootUser) {
      return alerts; // Root sees everything
    }
    return alerts.filter(alert => canRoleViewAlert(alert, userRole));
  }, [alerts, isRootUser, userRole]);
  
  // Get circuit timezone
  const circuitTimeZone = selectedEvent?.circuit?.time_zone;

  // Check if this is a non-legacy event (alerts work for both demo and regular non-legacy events)
  const isNonLegacyEvent = selectedEvent && !selectedEvent.legacy;
  
  // Check if this is a demo event
  const isDemo = selectedEvent?.demo ?? false;

  // Check if the event is currently active (ongoing) - only allow CRUD for active events
  // Uses circuit timezone for consistent behavior across the app
  const isEventActive = checkEventActive(selectedEvent);

  // User can manage alerts only if they have the role AND the event is active
  const canManageAlerts = hasAdminRole && isEventActive;

  // Wrapper function for refresh that supports silent refresh (button spinner only)
  const handleRefresh = useCallback(async (showRefreshing: boolean = false) => {
    if (showRefreshing) {
      setRefreshing(true);
    }
    try {
      await refreshAlerts();
      // Mark initial load as complete after first successful fetch
      if (!initialLoadComplete) {
        setInitialLoadComplete(true);
      }
    } finally {
      if (showRefreshing) {
        setRefreshing(false);
      }
    }
  }, [refreshAlerts, initialLoadComplete]);

  // Track when loading completes to mark initial load as done
  useEffect(() => {
    if (!loading) {
      // Mark initial load as complete when loading finishes
      if (!initialLoadComplete) {
        setInitialLoadComplete(true);
      }
    }
  }, [loading, initialLoadComplete]);

  // Reset initial load flag when event changes
  useEffect(() => {
    setInitialLoadComplete(false);
  }, [selectedEvent?.season_year, selectedEvent?.circuit_id, selectedEvent?.start_date]);

  // Measure table container and set table body scroll height (reserve for pagination)
  const TABLE_SCROLL_RESERVE_PX = 80;
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const updateScrollY = () => {
      if (!el.isConnected) return;
      const height = el.clientHeight;
      setTableScrollY(Math.max(200, height - TABLE_SCROLL_RESERVE_PX));
    };
    updateScrollY();
    const ro = new ResizeObserver(updateScrollY);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, visibleAlerts.length]);

  // Refresh alerts when tab becomes visible or window gains focus (for active events)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isEventActive) {
        console.log('[Alerts] Tab became visible, refreshing alerts');
        handleRefresh(true); // Silent refresh - show button spinner but no table loading overlay
      }
    };

    // Also handle window focus event for when returning from another application
    // (visibilitychange may not fire in all browsers when switching apps)
    const handleWindowFocus = () => {
      if (isEventActive) {
        console.log('[Alerts] Window gained focus, refreshing alerts');
        handleRefresh(true); // Silent refresh - show button spinner but no table loading overlay
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [isEventActive, handleRefresh]);

  // Periodic refresh every 5 minutes (for active events)
  useEffect(() => {
    if (!isEventActive || !selectedEvent) return;

    const interval = setInterval(() => {
      console.log('[Alerts] Periodic refresh (5 minutes)');
      handleRefresh(true); // Silent refresh - show button spinner but no table loading overlay
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [isEventActive, selectedEvent, handleRefresh]);

  // Helper function to find matching sessions based on alert time range
  // Returns all sessions that overlap with the alert's start_time to end_time range
  const findMatchingSessions = useCallback((
    alertStart: dayjs.Dayjs | null | undefined,
    alertEnd: dayjs.Dayjs | null | undefined
  ): string[] => {
    if (!alertStart || !selectedEvent?.sessions?.length) return [];
    
    const alertStartUtc = alertStart.utc();
    const alertEndUtc = alertEnd?.utc() || alertStartUtc; // If no end, use start as point-in-time
    
    const matchingSessions: string[] = [];
    
    for (const session of selectedEvent.sessions) {
      const sessionStart = dayjs(session.start_time).utc();
      const sessionEnd = dayjs(session.end_time).utc();
      
      // Check if alert range overlaps with session range
      // Overlap: alertStart <= sessionEnd AND alertEnd >= sessionStart
      if (alertStartUtc.isSameOrBefore(sessionEnd) && alertEndUtc.isSameOrAfter(sessionStart)) {
        matchingSessions.push(session.session_name);
      }
    }
    
    return matchingSessions;
  }, [selectedEvent?.sessions]);

  const handleCreate = () => {
    setEditingAlert(null);
    form.resetFields();
    // Set start_time to current time in circuit timezone, rounded to the minute (seconds = 0)
    const nowInCircuitTz = circuitTimeZone 
      ? dayjs().tz(circuitTimeZone).second(0).millisecond(0)
      : dayjs().second(0).millisecond(0);
    // Set end_time to 1 minute after start_time
    const endTimeDefault = nowInCircuitTz.add(1, 'minute');
    // Auto-select sessions based on current time
    const matchingSessions = findMatchingSessions(nowInCircuitTz, endTimeDefault);
    form.setFieldsValue({
      alert_type: 'general',
      severity: 'info',
      start_time: nowInCircuitTz, // Default to now in circuit timezone (rounded to minute)
      end_time: endTimeDefault, // Default to 1 minute after start
      sessions: matchingSessions, // Auto-select matching sessions
      roles: [], // Empty means visible to all roles
      show_immediately: true, // Default to show immediately for new alerts
      notify_before: [], // No advance notifications by default
      notify_on_start: false, // Don't notify on start by default
      notify_on_end: false, // Don't notify on end by default
      requires_acknowledgment: false,
    });
    setIsModalOpen(true);
  };

  const handleEdit = (alert: EventAlert) => {
    setEditingAlert(alert);
    // Convert UTC dates to circuit local time for display
    const startTime = alert.start_time 
      ? (circuitTimeZone ? dayjs(alert.start_time).tz(circuitTimeZone) : dayjs(alert.start_time))
      : undefined;
    const endTime = alert.end_time 
      ? (circuitTimeZone ? dayjs(alert.end_time).tz(circuitTimeZone) : dayjs(alert.end_time))
      : undefined;
    
    form.setFieldsValue({
      alert_type: alert.alert_type,
      severity: alert.severity,
      title: alert.title,
      message: alert.message,
      sessions: alert.sessions || [],
      roles: alert.roles || [],
      start_time: startTime,
      end_time: endTime,
      show_immediately: alert.show_immediately,
      notify_before: alert.notify_before || [],
      notify_on_start: alert.notify_on_start ?? false,
      notify_on_end: alert.notify_on_end ?? false,
      requires_acknowledgment: alert.requires_acknowledgment,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (alertId: string) => {
    try {
      const response = await alertsService.deleteAlert(alertId, isDemo);
      if (response.success) {
        message.success('Alert deleted successfully');
        handleRefresh(false); // Silent refresh after delete
      } else {
        message.error('Failed to delete alert');
      }
    } catch (err) {
      console.error('Error deleting alert:', err);
      message.error('Failed to delete alert');
    }
  };

  const handlePreviewAlert = useCallback((alert: EventAlert) => {
    const notificationKey = `preview-${alert.id}-${Date.now()}`;
    showAlertToast({
      api: notificationApi,
      alert,
      notificationKey,
      compact: compactAlerts,
      duration: 8,
      onAcknowledge: () => notificationApi.destroy(notificationKey),
    });
  }, [notificationApi, compactAlerts]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Check if creating a new alert that will be sent to all roles
      const allRoles = ['root', 'account-admin', 'collaborator', 'developer'];
      const selectedRoles = values.roles || [];
      const isAllRoles = !selectedRoles.length || 
        (selectedRoles.length === allRoles.length && 
         allRoles.every(role => selectedRoles.includes(role)));
      
      // Show confirmation dialog for new alerts sent to all roles
      if (!editingAlert && isAllRoles) {
        return new Promise<void>((resolve) => {
          Modal.confirm({
            title: 'Confirm Alert Creation',
            content: 'This alert will be sent to all user roles. Are you sure you want to create it?',
            okText: 'Yes, Create Alert',
            cancelText: 'Cancel',
            onOk: async () => {
              await performSubmit(values);
              resolve();
            },
            onCancel: () => {
              resolve();
            },
          });
        });
      }
      
      // Proceed with submission for edits or alerts with specific roles
      await performSubmit(values);
    } catch (err) {
      console.error('Error saving alert:', err);
      message.error('Failed to save alert');
    }
  };

  const performSubmit = async (values: AlertFormValues) => {
    try {
      setSubmitting(true);

      // Convert dates from circuit local time to UTC for storage
      // The DatePicker returns values in the browser's local timezone, but the user
      // is entering times they expect to be in the CIRCUIT timezone.
      // We need to:
      // 1. Get the raw time values (hour, minute, day, etc.) that the user entered
      // 2. Interpret them as being in the circuit timezone
      // 3. Convert to UTC for storage
      
      let startTimeUtc: string | undefined;
      let endTimeUtc: string;
      
      if (circuitTimeZone) {
        // Get the display values (what the user sees/entered), rounded to the minute
        if (values.start_time) {
          const displayFormat = values.start_time.format('YYYY-MM-DD HH:mm') + ':00';
          // Parse this as circuit timezone and convert to UTC
          startTimeUtc = dayjs.tz(displayFormat, circuitTimeZone).utc().toISOString();
        }
        
        const endDisplayFormat = values.end_time.format('YYYY-MM-DD HH:mm') + ':00';
        endTimeUtc = dayjs.tz(endDisplayFormat, circuitTimeZone).utc().toISOString();
      } else {
        // Fallback: no circuit timezone, treat as browser local time (rounded to minute)
        startTimeUtc = values.start_time?.second(0).millisecond(0).utc().toISOString();
        endTimeUtc = values.end_time.second(0).millisecond(0).utc().toISOString();
      }
      
      // Process notify_before entries
      const notifyBefore: NotifyBeforeEntry[] = (values.notify_before || [])
        .filter((entry: { value?: number; unit?: NotifyBeforeUnit }) => entry && entry.value && entry.unit)
        .map((entry: { value: number; unit: NotifyBeforeUnit }) => ({
          value: entry.value,
          unit: entry.unit,
        }));

      const alertData: CreateEventAlertRequest | UpdateEventAlertRequest = {
        alert_type: values.alert_type,
        severity: values.severity,
        title: values.title,
        message: values.message,
        sessions: values.sessions || [],
        roles: values.roles || [],
        start_time: startTimeUtc,
        end_time: endTimeUtc,
        show_immediately: values.show_immediately || false,
        notify_before: notifyBefore,
        notify_on_start: values.notify_on_start || false,
        notify_on_end: values.notify_on_end || false,
        requires_acknowledgment: values.requires_acknowledgment || false,
      };

      let response;
      if (editingAlert) {
        response = await alertsService.updateAlert(editingAlert.id, alertData, isDemo);
      } else {
        response = await alertsService.createAlert(
          selectedEvent!.season_year,
          selectedEvent!.circuit_id,
          selectedEvent!.start_date,
          alertData as CreateEventAlertRequest,
          isDemo
        );
      }

      if (response.success) {
        message.success(editingAlert ? 'Alert updated successfully' : 'Alert created successfully');
        setIsModalOpen(false);
        handleRefresh(false); // Silent refresh after create/update
      } else {
        message.error(editingAlert ? 'Failed to update alert' : 'Failed to create alert');
      }
    } catch (err) {
      console.error('Error saving alert:', err);
      message.error('Failed to save alert');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return '-';
    return dayjs(timestamp).tz(circuitTimeZone || 'UTC').format('YYYY-MM-DD HH:mm:ss');
  };

  const columns: ColumnsType<EventAlert> = [
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      render: (severity: AlertSeverity) => (
        <Tag 
          icon={getSeverityIcon(severity)}
          color={severity === 'critical' ? 'error' : severity === 'warning' ? 'warning' : 'processing'}
        >
          {SEVERITY_CONFIG[severity]?.label || severity}
        </Tag>
      ),
      filters: severityOptions.map(opt => ({ text: opt.label, value: opt.value })),
      onFilter: (value, record) => record.severity === value,
    },
    {
      title: 'Type',
      dataIndex: 'alert_type',
      key: 'alert_type',
      render: (alertType: AlertType) => {
        const config = ALERT_TYPE_CONFIG[alertType];
        return (
          <Tag>
            {config?.icon} {config?.label || alertType}
          </Tag>
        );
      },
      filters: alertTypeOptions.map(opt => ({ text: opt.label, value: opt.value })),
      onFilter: (value, record) => record.alert_type === value,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title: string) => (
        <Text strong>{title}</Text>
      ),
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (msg: string) => (
        <Tooltip title={msg}>
          <Text>{msg}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Sessions',
      dataIndex: 'sessions',
      key: 'sessions',
      render: (sessions: string[]) => sessions?.length > 0 
        ? sessions.map(s => <Tag key={s}>{s}</Tag>)
        : '-',
    },
    {
      title: 'Start Time',
      dataIndex: 'start_time',
      key: 'start_time',
      render: formatTimestamp,
      sorter: (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'End Time',
      dataIndex: 'end_time',
      key: 'end_time',
      render: (endTime: string | null) => endTime ? formatTimestamp(endTime) : '-',
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: formatTimestamp,
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      ellipsis: true,
      render: (createdBy: string, record: EventAlert) => (
        <Tooltip title={record.is_automated ? 'Automated' : 'Manual'}>
          <Text type={record.is_automated ? 'secondary' : undefined}>
            {record.is_automated ? `🤖 ${createdBy}` : createdBy}
          </Text>
        </Tooltip>
      ),
    },
  ];

  // Add Roles column only for root users
  if (isRootUser) {
    columns.push({
      title: 'Roles',
      dataIndex: 'roles',
      key: 'roles',
      render: (roles: string[]) => {
        if (!roles || roles.length === 0) {
          return <Tag color="green">All</Tag>;
        }
        return (
          <Tooltip title={roles.join(', ')}>
            <Space size={2}>
              {roles.slice(0, 2).map(r => <Tag key={r} color="blue">{r}</Tag>)}
              {roles.length > 2 && <Tag>+{roles.length - 2}</Tag>}
            </Space>
          </Tooltip>
        );
      },
    });
  }

  // Add Ack Required column only for admins/root
  if (canManageAlerts) {
    columns.push({
      title: 'Ack Required',
      dataIndex: 'requires_acknowledgment',
      key: 'requires_acknowledgment',
      render: (requiresAck: boolean) => (
        requiresAck ? <Tag color="orange">Yes</Tag> : <Tag>No</Tag>
      ),
    });
  }

  // Add actions column if user can manage alerts or preview for root
  if (canManageAlerts) {
    columns.push({
      title: 'Actions',
      key: 'actions',
      width: isRootUser ? 140 : 100,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          {isRootUser && (
            <Tooltip title="Preview notification">
              <Button
                type="text"
                icon={<NotificationOutlined />}
                onClick={() => handlePreviewAlert(record)}
                size="small"
              />
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Delete alert"
            description="Are you sure you want to delete this alert?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />} 
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    });
  } else if (isRootUser) {
    columns.push({
      title: 'Preview',
      key: 'preview',
      width: 70,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="Preview notification">
          <Button
            type="text"
            icon={<NotificationOutlined />}
            onClick={() => handlePreviewAlert(record)}
            size="small"
          />
        </Tooltip>
      ),
    });
  }

  // Show message for legacy events
  if (selectedEvent && selectedEvent.legacy) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '8px' }}>
        <Card style={{ flex: 1 }}>
          <Empty
            description={
              <span>
                Alerts are not available for legacy events.
                <br />
                Please view the Messages page for legacy event notifications.
              </span>
            }
          />
        </Card>
      </div>
    );
  }

  // Show message if no event selected
  if (!selectedEvent) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '8px' }}>
        <Card style={{ flex: 1 }}>
          <Empty
            description={
              <span>
                Please select a season and event to view alerts
              </span>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {notificationContextHolder}
      {/* Timeline with integrated header */}
      <div style={{ flexShrink: 0 }}>
        <AlertsTimeline
          alerts={visibleAlerts}
          sessions={selectedEvent?.sessions || []}
          eventStartDate={selectedEvent.start_date}
          eventEndDate={selectedEvent.end_date}
          circuitTimeZone={circuitTimeZone}
          loading={loading && !initialLoadComplete}
          refreshing={refreshing || (loading && initialLoadComplete)}
          onRefresh={() => handleRefresh(true)}
          onCreateAlert={canManageAlerts && isNonLegacyEvent ? handleCreate : undefined}
          canManageAlerts={!!(canManageAlerts && isNonLegacyEvent)}
          hasAdminRole={hasAdminRole}
          error={error}
          onClearError={() => setError(null)}
        />
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Show table loading overlay only on initial load, then silent refresh after */}
        {loading && !initialLoadComplete ? (
          <Card style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </Card>
        ) : visibleAlerts.length === 0 ? (
          <Card style={{ flex: 1 }}>
            <Empty description="No alerts found for this event" />
          </Card>
        ) : (
          <Card style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 0 }} styles={{ body: { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}>
            <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
              <Table
                dataSource={visibleAlerts}
                columns={columns}
                rowKey="id"
                pagination={{ 
                  pageSize: 20,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} alerts`
                }}
                size="middle"
                scroll={{ x: 1200, ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                style={{ flex: 1 }}
              />
            </div>
          </Card>
        )}
      </div>

      {/* Create/Edit Modal */}
      <AlertModal
        open={isModalOpen}
        editingAlert={editingAlert}
        form={form}
        submitting={submitting}
        onCancel={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        selectedEvent={selectedEvent}
        circuitTimeZone={circuitTimeZone}
        isRootUser={isRootUser}
        findMatchingSessions={findMatchingSessions}
      />
    </div>
  );
};

